import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Plus,
  Search,
  Upload,
  Download,
  Trash2,
  KanbanSquare,
  List,
  X,
  SlidersHorizontal,
  StickyNote,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../context/LanguageContext";
import { useStageLabel } from "../../context/CRMSettingsContext";
import { tCrm, crmApi, formatCurrency, STAGES, STAGE_COLORS, TEMPERATURE_META, logDevError } from "../../lib/crm";
import { toast } from "sonner";

const PRE_QUALIFY_STAGES = ["new", "contacting", "no_response", "call_back", "contacted", "meeting_scheduled"];

export default function CRMLeads({ me }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const stageLabel = useStageLabel();
  const navigate = useNavigate();
  const isAdmin = me.role === "admin";

  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Ghost mode: admin views another agent's pipeline read-only.
  // Triggered via URL: /crm/leads?agent=<id>&ghost=1
  const ghostAgentId = isAdmin ? searchParams.get("agent") : null;
  const ghostMode = Boolean(isAdmin && ghostAgentId && searchParams.get("ghost") === "1");

  const [agentFilter, setAgentFilter] = useState(ghostAgentId || "");
  const [view, setView] = useState("kanban");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [stageFilter, setStageFilter] = useState(""); // "" | "pending_contact" | "pending_qualify"
  const [hasEmailOnly, setHasEmailOnly] = useState(false);
  const [hasPhoneOnly, setHasPhoneOnly] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [filters, setFilters] = useState({
    temperature: "",
    source: "",
    country: "",
    tag: "",
    min_value: "",
    max_value: "",
    inactive_days: "",
    priority_score: "",
    category: "",
    phone: "",
    created_from: "",
    created_to: "",
  });

  const fetchLeads = useCallback(async () => {
    const params = { include_lost: false };
    if (search) params.search = search;
    if (agentFilter) params.agent_id = agentFilter;
    const keys = ["temperature", "source", "country", "tag", "min_value", "max_value", "inactive_days", "priority_score", "category", "phone", "created_from", "created_to"];
    for (const k of keys) {
      const v = filters[k];
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    }
    const r = await crmApi.get("/leads", { params });
    setLeads(r.data);
    // Refresh known-countries list ONLY when no country filter is active,
    // so the country dropdown keeps every option even after filtering.
    if (!filters.country) {
      const set = new Set();
      for (const l of r.data) {
        const c = (l.country || "").trim();
        if (c) set.add(c);
      }
      setAllCountries(Array.from(set).sort((a, b) => a.localeCompare(b)));
    }
    setLoading(false);
  }, [search, agentFilter, filters]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    if (isAdmin) {
      crmApi.get("/agents").then((r) => setAgents(r.data)).catch((err) => logDevError("CRMLeads.agents", err));
    }
  }, [isAdmin]);

  const activeFiltersCount = ["temperature", "source", "country", "tag", "min_value", "max_value", "inactive_days", "priority_score", "category", "phone", "created_from", "created_to"]
    .filter((k) => filters[k] !== "" && filters[k] !== null && filters[k] !== undefined).length
    + (hasEmailOnly ? 1 : 0) + (hasPhoneOnly ? 1 : 0);

  const clearFilters = () => {
    setFilters({ temperature: "", source: "", country: "", tag: "", min_value: "", max_value: "", inactive_days: "", priority_score: "", category: "", phone: "", created_from: "", created_to: "" });
    setHasEmailOnly(false);
    setHasPhoneOnly(false);
  };

  // Visible leads after client-side stage + contact-completeness filters
  const visibleLeads = useMemo(() => {
    let list = leads;
    if (stageFilter === "pending_contact") list = list.filter((l) => l.stage === "new");
    else if (stageFilter === "pending_qualify") list = list.filter((l) => PRE_QUALIFY_STAGES.includes(l.stage));
    if (hasEmailOnly) list = list.filter((l) => !!(l.contact_email && String(l.contact_email).trim()));
    if (hasPhoneOnly) list = list.filter((l) => {
      const p = (l.contact_phone || l.whatsapp || "").toString().trim();
      return !!p;
    });
    return list;
  }, [leads, stageFilter, hasEmailOnly, hasPhoneOnly]);

  const groupedByStage = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s, []]));
    for (const ld of visibleLeads) {
      if (g[ld.stage]) g[ld.stage].push(ld);
    }
    return g;
  }, [visibleLeads]);

  const onDragEnd = async (result) => {
    if (ghostMode) {
      // Ghost mode is read-only — never persist any changes
      toast.info(t("ghost.readonly"));
      return;
    }
    if (!result.destination) return;
    const newStage = result.destination.droppableId;
    const oldStage = result.source.droppableId;
    if (newStage === oldStage) return;
    const leadId = result.draggableId;
    // Optimistic update — counters react instantly
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, stage: newStage } : l)));
    try {
      await crmApi.put(`/leads/${leadId}`, { stage: newStage });
      toast.success("Stage updated");
    } catch {
      toast.error(t("common.error"));
      fetchLeads();
    }
  };

  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.full_name])), [agents]);
  const unassignedCount = useMemo(
    () => leads.filter((l) => !l.assigned_agent_id).length,
    [leads]
  );
  const pendingContactCount = useMemo(
    () => leads.filter((l) => l.stage === "new").length,
    [leads]
  );
  const pendingQualifyCount = useMemo(
    () => leads.filter((l) => PRE_QUALIFY_STAGES.includes(l.stage)).length,
    [leads]
  );
  // Distinct countries kept in `allCountries` state — populated on every fetch where
  // no country filter is active, so the dropdown keeps every option visible.
  const availableCountries = allCountries;

  // Resolve the ghost agent's display name (for the banner)
  const ghostAgent = useMemo(
    () => (ghostMode ? agents.find((a) => a.id === ghostAgentId) : null),
    [ghostMode, ghostAgentId, agents]
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {ghostMode && (
        <div
          data-testid="ghost-banner"
          className="sticky top-2 z-30 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-violet-400/40 bg-violet-500/15 backdrop-blur-md"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="text-xl leading-none">👻</span>
            <div>
              <div className="font-semibold text-violet-200">
                {t("ghost.title")}: {ghostAgent?.full_name || ghostAgentId}
              </div>
              <div className="text-[11px] text-white/50 uppercase tracking-wider mono">
                {t("ghost.subtitle")}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setSearchParams({});
              setAgentFilter("");
            }}
            data-testid="ghost-exit-btn"
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white"
          >
            ✕ {t("ghost.exit")}
          </button>
        </div>
      )}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("leads.title")}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-sm text-white/40">{visibleLeads.length} {t("leads.title").toLowerCase()}</p>
            {isAdmin && unassignedCount > 0 && (
              <button
                onClick={() => setAgentFilter(agentFilter === "__unassigned__" ? "" : "__unassigned__")}
                data-testid="unassigned-counter-chip"
                className={`text-xs px-2 py-0.5 rounded-full border transition ${
                  agentFilter === "__unassigned__"
                    ? "bg-amber-500/30 border-amber-400/60 text-amber-200"
                    : "bg-amber-500/15 border-amber-500/30 text-amber-300 hover:bg-amber-500/20"
                }`}
                title="Click para filtrar solo los leads sin asignar"
              >
                ⚠️ {unassignedCount} {t("chips.unassigned")}
              </button>
            )}
            {pendingContactCount > 0 && (
              <button
                onClick={() => setStageFilter(stageFilter === "pending_contact" ? "" : "pending_contact")}
                data-testid="pending-contact-chip"
                className={`text-xs px-2 py-0.5 rounded-full border transition ${
                  stageFilter === "pending_contact"
                    ? "bg-sky-500/30 border-sky-400/60 text-sky-200"
                    : "bg-sky-500/15 border-sky-500/30 text-sky-300 hover:bg-sky-500/25"
                }`}
                title="Click para ver solo leads pendientes de contactar"
              >
                📞 {pendingContactCount} {t("chips.pendingContact")}
              </button>
            )}
            {pendingQualifyCount > 0 && (
              <button
                onClick={() => setStageFilter(stageFilter === "pending_qualify" ? "" : "pending_qualify")}
                data-testid="pending-qualify-chip"
                className={`text-xs px-2 py-0.5 rounded-full border transition ${
                  stageFilter === "pending_qualify"
                    ? "bg-fuchsia-500/30 border-fuchsia-400/60 text-fuchsia-200"
                    : "bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25"
                }`}
                title="Leads aún no calificados — click para filtrar"
              >
                🎯 {pendingQualifyCount} {t("chips.pendingQualify")}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
            <Input
              placeholder={t("leads.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="leads-search-input"
              className="pl-9 bg-white/5 border-white/10 text-white w-72"
            />
          </div>
          {isAdmin && !ghostMode && (
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              data-testid="leads-agent-filter"
              className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2"
            >
              <option value="">All agents</option>
              <option value="__unassigned__">
                ⚠️ Sin asignar{unassignedCount > 0 ? ` (${unassignedCount})` : ""}
              </option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          )}
          <Button
            onClick={() => setShowFilters((v) => !v)}
            variant="outline"
            data-testid="filters-toggle-btn"
            className="border-white/10 text-white/80 relative"
          >
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-lime-400 text-white">
                {activeFiltersCount}
              </span>
            )}
          </Button>
          <div className="flex border border-white/10 rounded-md overflow-hidden">
            <button
              onClick={() => setView("kanban")}
              data-testid="view-kanban-btn"
              className={`px-2 py-2 ${view === "kanban" ? "bg-white/10" : "hover:bg-white/5"}`}
              title="Kanban"
            >
              <KanbanSquare className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list-btn"
              className={`px-2 py-2 ${view === "list" ? "bg-white/10" : "hover:bg-white/5"}`}
              title="List"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          {isAdmin && !ghostMode && (
            <Button
              onClick={() => setShowCsvImport(true)}
              variant="outline"
              size="sm"
              data-testid="csv-import-btn"
              className="border-white/10 text-white/80"
            >
              <Upload className="w-3.5 h-3.5 mr-1" />{t("leads.importCsv")}
            </Button>
          )}
          <Button
            onClick={async () => {
              try {
                const res = await crmApi.get("/leads/export/csv", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const stamp = new Date().toISOString().slice(0, 10);
                a.download = `crm_leads_${stamp}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success(t("leads.exportOk") || "✓");
              } catch (err) {
                logDevError("CRMLeads.exportCsv", err);
                toast.error(t("common.error"));
              }
            }}
            variant="outline"
            size="sm"
            data-testid="csv-export-btn"
            className="border-white/10 text-white/80"
          >
            <Download className="w-3.5 h-3.5 mr-1" />{t("leads.exportCsv") || "Export CSV"}
          </Button>
          {!ghostMode && (
            <Button
              onClick={() => setShowCreate(true)}
              data-testid="new-lead-btn"
              className="bg-lime-400 hover:bg-lime-300 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t("leads.newLead")}
            </Button>
          )}
        </div>
      </header>

      {showFilters && (
        <div className="glass-strong rounded-xl p-4 border border-white/8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="filters-panel">
          <FilterField
            label="Temperature"
            value={filters.temperature}
            onChange={(v) => setFilters({ ...filters, temperature: v })}
            options={Object.keys(TEMPERATURE_META)}
            testid="filter-temperature"
          />
          <FilterField
            label="Priority"
            value={filters.priority_score}
            onChange={(v) => setFilters({ ...filters, priority_score: v })}
            options={["HIGH", "MEDIUM", "LOW"]}
            testid="filter-priority-score"
          />
          <FilterField
            label="Category"
            value={filters.category}
            onChange={(v) => setFilters({ ...filters, category: v })}
            testid="filter-category"
          />
          <FilterField
            label="Source"
            value={filters.source}
            onChange={(v) => setFilters({ ...filters, source: v })}
            options={["Web", "Referral", "LinkedIn", "Event", "Email", "Cold call", "Ad", "Other"]}
            testid="filter-source"
          />
          <FilterField
            label="Country"
            value={filters.country}
            onChange={(v) => setFilters({ ...filters, country: v })}
            options={availableCountries}
            testid="filter-country"
          />
          <FilterField
            label="Tag"
            value={filters.tag}
            onChange={(v) => setFilters({ ...filters, tag: v })}
            testid="filter-tag"
          />
          <FilterField
            label="Min value (€)"
            type="number"
            value={filters.min_value}
            onChange={(v) => setFilters({ ...filters, min_value: v })}
            testid="filter-min-value"
          />
          <FilterField
            label="Max value (€)"
            type="number"
            value={filters.max_value}
            onChange={(v) => setFilters({ ...filters, max_value: v })}
            testid="filter-max-value"
          />
          <FilterField
            label="Inactive > days"
            type="number"
            value={filters.inactive_days}
            onChange={(v) => setFilters({ ...filters, inactive_days: v })}
            testid="filter-inactive"
          />
          <FilterField
            label={language === "es" ? "Teléfono / WhatsApp" : "Phone / WhatsApp"}
            type="tel"
            value={filters.phone}
            onChange={(v) => setFilters({ ...filters, phone: v })}
            testid="filter-phone"
          />
          <FilterField
            label={language === "es" ? "Creado desde" : "Created from"}
            type="date"
            value={filters.created_from}
            onChange={(v) => setFilters({ ...filters, created_from: v })}
            testid="filter-created-from"
          />
          <FilterField
            label={language === "es" ? "Creado hasta" : "Created to"}
            type="date"
            value={filters.created_to}
            onChange={(v) => setFilters({ ...filters, created_to: v })}
            testid="filter-created-to"
          />
          <div className="flex items-end gap-4 sm:col-span-2 lg:col-span-4 flex-wrap border-t border-white/8 pt-3 mt-1">
            <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none hover:text-white" data-testid="filter-has-email-label">
              <input
                type="checkbox"
                checked={hasEmailOnly}
                onChange={(e) => setHasEmailOnly(e.target.checked)}
                data-testid="filter-has-email"
                className="accent-lime-400 w-4 h-4"
              />
              <span>📧 {language === "es" ? "Solo con email" : "Only with email"}</span>
            </label>
            <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none hover:text-white" data-testid="filter-has-phone-label">
              <input
                type="checkbox"
                checked={hasPhoneOnly}
                onChange={(e) => setHasPhoneOnly(e.target.checked)}
                data-testid="filter-has-phone"
                className="accent-lime-400 w-4 h-4"
              />
              <span>📞 {language === "es" ? "Solo con teléfono" : "Only with phone"}</span>
            </label>
            <button
              onClick={clearFilters}
              data-testid="filter-clear-btn"
              className="text-xs text-lime-400 hover:text-lime-300 ml-auto"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">{t("common.loading")}</div>
      ) : view === "kanban" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <KanbanScrollWrapper>
            {(refSetter) => (
              <div
                ref={refSetter}
                className="flex gap-4 overflow-x-auto pb-2 kanban-scroll"
                data-testid="kanban-board"
              >
            {STAGES.map((stage) => (
              <Droppable key={stage} droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    data-testid={`kanban-col-${stage}`}
                    className={`min-w-[260px] w-[260px] rounded-xl border ${
                      snapshot.isDraggingOver ? "border-lime-400/50 bg-lime-400/5" : "border-white/8 bg-white/[0.02]"
                    }`}
                  >
                    <div
                      className="px-3 py-2.5 border-b border-white/8 flex items-center justify-between text-xs"
                      style={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[stage] }} />
                        <span className="font-semibold uppercase tracking-wider text-white/80">
                          {stageLabel(stage, language, t(`stage.${stage}`))}
                        </span>
                      </div>
                      <span className="text-white/40 mono">{groupedByStage[stage].length}</span>
                    </div>
                    <div className="p-2 space-y-2 min-h-[120px]">
                      {groupedByStage[stage].map((lead, idx) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={idx}>
                          {(p, snap) => (
                            <KanbanCard
                              p={p}
                              snap={snap}
                              lead={lead}
                              isAdmin={isAdmin}
                              ghost={ghostMode}
                              agentMap={agentMap}
                              onClick={() => navigate(`/crm/leads/${lead.id}`)}
                              onNoteSaved={(noteText) => {
                                // optimistic local update so UI stays snappy
                                setLeads((curr) =>
                                  curr.map((l) =>
                                    l.id === lead.id ? { ...l, internal_notes: noteText } : l
                                  )
                                );
                              }}
                              t={t}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
              </div>
            )}
          </KanbanScrollWrapper>
        </DragDropContext>
      ) : (
        <LeadsTable leads={leads} agentMap={agentMap} agents={agents} isAdmin={isAdmin && !ghostMode} onRefresh={fetchLeads} stageLabel={stageLabel} language={language} />
      )}

      {showCreate && (
        <CreateLeadDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            fetchLeads();
          }}
          agents={agents}
          isAdmin={isAdmin}
        />
      )}

      {showCsvImport && (
        <CsvImportDialog
          onClose={() => setShowCsvImport(false)}
          onImported={() => {
            setShowCsvImport(false);
            fetchLeads();
          }}
        />
      )}
    </div>
  );
}


function KanbanScrollWrapper({ children }) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [metrics, setMetrics] = useState({ scrollLeft: 0, scrollWidth: 1, clientWidth: 1 });
  const draggingRef = useRef(null);

  const refSetter = useCallback((node) => {
    scrollRef.current = node;
  }, []);

  // Read scroll metrics from the inner kanban div
  const sync = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMetrics({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener("resize", sync);
    // Also re-sync after children mount (cards/columns may resize)
    const t1 = setTimeout(sync, 100);
    const t2 = setTimeout(sync, 500);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
      window.removeEventListener("resize", sync);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [sync]);

  const ratio = Math.min(1, metrics.clientWidth / Math.max(1, metrics.scrollWidth));
  const thumbWidthPct = ratio * 100;
  const maxScroll = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  const thumbLeftPct = maxScroll > 0 ? (metrics.scrollLeft / maxScroll) * (100 - thumbWidthPct) : 0;
  const showScrollbar = metrics.scrollWidth > metrics.clientWidth + 2;

  // Drag thumb to scroll
  const onThumbMouseDown = (e) => {
    e.preventDefault();
    const track = trackRef.current;
    const el = scrollRef.current;
    if (!track || !el) return;
    const trackRect = track.getBoundingClientRect();
    const usableWidth = trackRect.width * (1 - ratio);
    draggingRef.current = {
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      usableWidth,
      maxScroll,
    };
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = draggingRef.current;
      if (!d || !scrollRef.current) return;
      const dx = e.clientX - d.startX;
      const ratioMoved = d.usableWidth > 0 ? dx / d.usableWidth : 0;
      scrollRef.current.scrollLeft = d.startScrollLeft + ratioMoved * d.maxScroll;
    };
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = null;
        document.body.style.userSelect = "";
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Click on track jumps the thumb
  const onTrackClick = (e) => {
    if (e.target !== trackRef.current) return;
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const targetCenter = clickX / rect.width;
    el.scrollTo({ left: targetCenter * maxScroll, behavior: "smooth" });
  };

  const scrollByCols = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="kanban-scroll-wrapper">
      {/* Top navigation bar — always visible above the kanban */}
      {showScrollbar && (
        <div className="flex items-center gap-2 mb-2 select-none" data-testid="kanban-scrollbar">
          <button
            type="button"
            onClick={() => scrollByCols(-1)}
            disabled={metrics.scrollLeft <= 0}
            className="shrink-0 w-9 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-lime-400/20 border border-white/10 text-white/70 hover:text-lime-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-lg font-bold"
            aria-label="Scroll left"
            data-testid="kanban-scroll-left"
          >
            ‹
          </button>
          <div
            ref={trackRef}
            onClick={onTrackClick}
            className="relative flex-1 h-5 rounded-full bg-white/[0.06] border border-white/10 cursor-pointer"
            data-testid="kanban-scroll-track"
          >
            <div
              onMouseDown={onThumbMouseDown}
              style={{
                left: `${thumbLeftPct}%`,
                width: `${Math.max(thumbWidthPct, 6)}%`,
              }}
              className="absolute top-0 bottom-0 rounded-full bg-gradient-to-b from-lime-300 via-lime-400 to-lime-500 shadow-[0_0_0_1px_rgba(163,230,53,0.6),0_2px_10px_rgba(163,230,53,0.35)] cursor-grab active:cursor-grabbing"
              data-testid="kanban-scroll-thumb"
            />
          </div>
          <button
            type="button"
            onClick={() => scrollByCols(1)}
            disabled={metrics.scrollLeft >= maxScroll - 1}
            className="shrink-0 w-9 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-lime-400/20 border border-white/10 text-white/70 hover:text-lime-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-lg font-bold"
            aria-label="Scroll right"
            data-testid="kanban-scroll-right"
          >
            ›
          </button>
          <span className="text-[10px] uppercase tracking-wider text-white/40 ml-2 mono shrink-0">
            {Math.round((metrics.scrollLeft / Math.max(1, maxScroll)) * 100)}%
          </span>
        </div>
      )}

      {/* Kanban itself */}
      <div className="relative">
        {children(refSetter)}

        {/* Floating arrow buttons overlayed on the kanban edges */}
        {showScrollbar && metrics.scrollLeft > 4 && (
          <button
            type="button"
            onClick={() => scrollByCols(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-14 flex items-center justify-center rounded-r-lg bg-black/60 hover:bg-lime-400/30 border border-l-0 border-white/15 text-white/80 hover:text-lime-300 backdrop-blur-sm transition-all shadow-lg text-xl font-bold"
            aria-label="Scroll left"
            data-testid="kanban-floating-left"
          >
            ‹
          </button>
        )}
        {showScrollbar && metrics.scrollLeft < maxScroll - 4 && (
          <button
            type="button"
            onClick={() => scrollByCols(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-14 flex items-center justify-center rounded-l-lg bg-black/60 hover:bg-lime-400/30 border border-r-0 border-white/15 text-white/80 hover:text-lime-300 backdrop-blur-sm transition-all shadow-lg text-xl font-bold"
            aria-label="Scroll right"
            data-testid="kanban-floating-right"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
}

function KanbanCard({ p, snap, lead, isAdmin, ghost = false, agentMap, onClick, onNoteSaved, t }) {
  const [editing, setEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(lead.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const trimmedNote = (lead.internal_notes || "").trim();

  const saveNote = async (e) => {
    e?.stopPropagation();
    setSaving(true);
    try {
      const next = noteDraft.trim();
      await crmApi.put(`/leads/${lead.id}`, { internal_notes: next });
      onNoteSaved(next);
      toast.success(t("leads.noteSaved") || "✓");
      setEditing(false);
    } catch (err) {
      logDevError("save kanban note", err);
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={p.innerRef}
      {...p.draggableProps}
      {...p.dragHandleProps}
      onClick={editing ? undefined : onClick}
      data-testid={`lead-card-${lead.id}`}
      className={`rounded-lg p-3 bg-[#0F0F17] border ${
        snap.isDragging ? "border-lime-400/50 shadow-lg" : "border-white/8 hover:border-white/20"
      } ${editing ? "" : "cursor-pointer"} transition`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-white truncate">{lead.company_name}</div>
          {lead.contact_full_name && (
            <div className="text-xs text-white/50 truncate mt-0.5">{lead.contact_full_name}</div>
          )}
        </div>
        {!editing && !ghost && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setNoteDraft(lead.internal_notes || "");
              setEditing(true);
            }}
            data-testid={`kanban-note-edit-${lead.id}`}
            title={t("leads.editNote") || "Edit quick note"}
            className={`shrink-0 p-1 rounded transition-colors ${
              trimmedNote ? "text-amber-300 hover:text-amber-200" : "text-white/30 hover:text-white/60"
            }`}
          >
            <StickyNote className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {lead.priority_score && (
        <span
          data-testid={`priority-badge-${lead.id}`}
          className={`inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded mt-1 mono ${
            lead.priority_score === "HIGH"
              ? "bg-red-500/20 text-red-300 border border-red-500/30"
              : lead.priority_score === "MEDIUM"
              ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
              : "bg-slate-500/20 text-slate-300 border border-slate-500/30"
          }`}
        >
          {lead.priority_score}
        </span>
      )}

      {/* Quick note display — full text, scrollable if very long */}
      {!editing && trimmedNote && (
        <div
          data-testid={`kanban-note-text-${lead.id}`}
          className="mt-2 px-2 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200/90 leading-snug whitespace-pre-wrap break-words max-h-48 overflow-y-auto"
        >
          {trimmedNote}
        </div>
      )}

      {/* Quick note inline editor */}
      {editing && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="mt-2 space-y-1.5"
        >
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder={t("leads.notePlaceholder")}
            data-testid={`kanban-note-input-${lead.id}`}
            rows={5}
            maxLength={1000}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEditing(false);
              } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                saveNote();
              }
            }}
            className="w-full text-xs bg-zinc-950 border border-amber-500/40 text-white rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1">
              {[
                t("leads.tagNoAnswer"),
                t("leads.tagCallBack"),
                t("leads.tagInterested"),
                t("leads.tagNotInterested"),
                t("leads.tagWrongNumber"),
              ].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setNoteDraft(tag)}
                  data-testid={`kanban-note-quick-${lead.id}-${tag}`}
                  className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              data-testid={`kanban-note-cancel-${lead.id}`}
              className="text-[10px] text-white/50 hover:text-white px-2 py-1"
            >
              {t("common.cancel") || "Cancel"}
            </button>
            <button
              type="button"
              onClick={saveNote}
              disabled={saving}
              data-testid={`kanban-note-save-${lead.id}`}
              className="ml-auto text-[10px] uppercase tracking-wider px-2.5 py-1 rounded bg-amber-400 text-black font-semibold hover:bg-amber-300 disabled:opacity-50"
            >
              {saving ? "…" : (t("common.save") || "Save")}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <span className="text-xs font-mono text-emerald-300/80">
          {formatCurrency(lead.deal_value, lead.currency)}
        </span>
        {lead.temperature && TEMPERATURE_META[lead.temperature] && (
          <span className="text-base" title={lead.temperature}>
            {TEMPERATURE_META[lead.temperature].icon}
          </span>
        )}
      </div>

      {isAdmin && lead.assigned_agent_id && (
        <div className="text-[10px] text-white/30 mt-2 mono">
          → {agentMap[lead.assigned_agent_id] || "—"}
        </div>
      )}
    </div>
  );
}


function LeadsTable({ leads, agentMap, agents, isAdmin, onRefresh, stageLabel, language }) {
  const t = (k) => tCrm(language, k);
  const navigate = useNavigate();
  const [selected, setSelected] = useState(new Set());
  const [bulkAgent, setBulkAgent] = useState("");
  const [confirmState, setConfirmState] = useState(null); // {preview, ids, target} or null
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  // Bulk assign — opens a modal if any lead is already on another agent;
  // otherwise runs immediately.
  const bulkAssign = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const target = bulkAgent || null;
    setBulkLoading(true);
    try {
      const preview = await crmApi.post("/leads/bulk-assign/preview", {
        lead_ids: ids,
        agent_id: target,
      });
      const p = preview.data || {};
      if ((p.already_assigned_other || 0) > 0) {
        // Show modal — admin chooses force vs skip
        setConfirmState({ preview: p, ids, target });
        return;
      }
      // Straight path: nothing in another agent → just call backend
      const r = await crmApi.post("/leads/bulk-assign", {
        lead_ids: ids,
        agent_id: target,
        force: false,
      });
      toast.success(`✓ ${r.data.updated} · ⏭ ${r.data.skipped_already_assigned}`);
      setSelected(new Set());
      setBulkAgent("");
      onRefresh();
    } catch (e) {
      logDevError("bulkAssign", e);
      toast.error(t("common.error"));
    } finally {
      setBulkLoading(false);
    }
  };

  const runBulkAssign = async (force) => {
    if (!confirmState) return;
    setBulkLoading(true);
    try {
      const r = await crmApi.post("/leads/bulk-assign", {
        lead_ids: confirmState.ids,
        agent_id: confirmState.target,
        force,
      });
      const msg = force
        ? `✓ ${r.data.updated} (force)`
        : `✓ ${r.data.updated} · ⏭ ${r.data.skipped_already_assigned}`;
      toast.success(msg);
      setConfirmState(null);
      setSelected(new Set());
      setBulkAgent("");
      onRefresh();
    } catch (e) {
      logDevError("runBulkAssign", e);
      toast.error(t("common.error"));
    } finally {
      setBulkLoading(false);
    }
  };

  // Inline single-row reassign from the table (admin only)
  const inlineReassign = async (leadId, newAgentId, force = false) => {
    try {
      const res = await crmApi.put(`/leads/${leadId}/assign`, {
        agent_id: newAgentId || null,
        force,
      });
      if (res.data?.no_op) {
        toast.info(t("assign.alreadyAssignedHere"));
      } else if (res.data?.reassigned) {
        toast.success(t("assign.reassignedOk"));
      } else {
        toast.success(t("assign.assignedOk"));
      }
      onRefresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "already_assigned") {
        const name = detail.current_agent?.full_name || "—";
        if (
          window.confirm(
            `${t("assign.confirmReassignPrefix")} ${name}.\n\n${t("assign.confirmReassignSuffix")}`
          )
        ) {
          await inlineReassign(leadId, newAgentId, true);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : t("common.error"));
      }
    }
  };

  if (leads.length === 0) {
    return <div className="text-white/40 text-sm p-6">{t("leads.empty")}</div>;
  }

  return (
    <div className="space-y-3">
      {isAdmin && selected.size > 0 && (
        <div
          data-testid="bulk-bar"
          className="sticky top-16 z-20 glass-strong rounded-xl border border-lime-400/40 bg-lime-400/10 px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <span className="text-sm font-semibold">{selected.size} {t("assign.selected")}</span>
          <select
            value={bulkAgent}
            onChange={(e) => setBulkAgent(e.target.value)}
            data-testid="bulk-agent-select"
            className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-1.5"
          >
            <option value="">{t("leads.unassigned")}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.full_name}</option>
            ))}
          </select>
          <Button
            onClick={bulkAssign}
            disabled={bulkLoading}
            data-testid="bulk-assign-btn"
            size="sm"
            className="bg-lime-400 hover:bg-lime-300 text-white disabled:opacity-50"
          >
            {bulkLoading ? "…" : t("assign.assignBtn")}
          </Button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-white/60 hover:text-white ml-auto"
            data-testid="bulk-clear-btn"
          >
            {t("assign.clear")}
          </button>
        </div>
      )}
      {confirmState && (
        <BulkReassignDialog
          state={confirmState}
          agents={agents}
          loading={bulkLoading}
          onForce={() => runBulkAssign(true)}
          onSkip={() => runBulkAssign(false)}
          onCancel={() => setConfirmState(null)}
          t={t}
        />
      )}
      <div className="overflow-x-auto glass-strong rounded-xl border border-white/8">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/50">
            <tr>
              {isAdmin && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === leads.length && leads.length > 0}
                    onChange={toggleAll}
                    data-testid="bulk-select-all"
                    aria-label="Select all"
                  />
                </th>
              )}
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-right px-4 py-3">Value</th>
              {isAdmin && <th className="text-left px-4 py-3">Agent</th>}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {leads.map((l) => (
              <tr
                key={l.id}
                className={`hover:bg-white/[0.03] ${selected.has(l.id) ? "bg-lime-400/5" : ""}`}
                data-testid={`lead-row-${l.id}`}
              >
                {isAdmin && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(l.id)}
                      onChange={() => toggle(l.id)}
                      data-testid={`bulk-select-${l.id}`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${l.company_name}`}
                    />
                  </td>
                )}
                <td onClick={() => navigate(`/crm/leads/${l.id}`)} className="px-4 py-3 font-medium cursor-pointer">{l.company_name}</td>
                <td onClick={() => navigate(`/crm/leads/${l.id}`)} className="px-4 py-3 text-white/60 cursor-pointer">{l.contact_full_name || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full" style={{ background: STAGE_COLORS[l.stage] + "20", color: STAGE_COLORS[l.stage] }}>
                    {stageLabel ? stageLabel(l.stage, language, t(`stage.${l.stage}`)) : t(`stage.${l.stage}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono">{formatCurrency(l.deal_value, l.currency)}</td>
                {isAdmin && (
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.assigned_agent_id || ""}
                      onChange={(e) => inlineReassign(l.id, e.target.value)}
                      data-testid={`inline-assign-${l.id}`}
                      className="bg-white/5 border border-white/10 text-white/80 text-xs rounded px-2 py-1 max-w-[160px]"
                      title={t("leads.reassignTitle") || "Reassign"}
                    >
                      <option value="">— {t("leads.unassigned")}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.full_name}</option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="px-4 py-3 text-right">
                  <Link to={`/crm/leads/${l.id}`} className="text-lime-400 text-xs">{t("leads.view")}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateLeadDialog({ onClose, onCreated, agents, isAdmin }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [form, setForm] = useState({
    company_name: "",
    contact_full_name: "",
    contact_email: "",
    contact_phone: "",
    deal_value: 0,
    assigned_agent_id: null,
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await crmApi.post("/leads", form);
      toast.success("Lead created");
      onCreated();
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl p-6 w-full max-w-md border border-white/10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">{t("leads.newLead")}</h2>
          <button onClick={onClose} data-testid="new-lead-close" className="text-white/50 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Company *" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required testid="new-lead-company" />
          <Field label="Contact name" value={form.contact_full_name} onChange={(v) => setForm({ ...form, contact_full_name: v })} testid="new-lead-contact" />
          <Field label="Contact email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
          <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
          <Field label="Deal value (€)" type="number" value={form.deal_value} onChange={(v) => setForm({ ...form, deal_value: parseFloat(v) || 0 })} />
          {isAdmin && (
            <div>
              <Label className="text-white/70 text-xs">{t("leads.assignedTo")}</Label>
              <select
                value={form.assigned_agent_id || ""}
                onChange={(e) => setForm({ ...form, assigned_agent_id: e.target.value || null })}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
              >
                <option value="">{t("leads.unassigned")}</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-white/80">{t("common.cancel")}</Button>
            <Button type="submit" data-testid="new-lead-submit" className="flex-1 bg-lime-400 hover:bg-lime-300 text-white">
              {t("common.create")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CsvImportDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignedAgentId, setAssignedAgentId] = useState("");

  useEffect(() => {
    // Load active agents so admin can assign the import to one of them upfront.
    // Silent on error — the selector just stays empty.
    crmApi.get("/agents").then((r) => {
      const list = (r.data || []).filter((a) => a.is_active !== false);
      setAgents(list);
    }).catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      if (assignedAgentId) form.append("assigned_agent_id", assignedAgentId);
      const r = await crmApi.post("/leads/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      const ins = r.data?.inserted || 0;
      const dups = r.data?.skipped_duplicates || 0;
      const assignedTo = agents.find((a) => a.id === assignedAgentId)?.full_name;
      toast.success(
        assignedTo
          ? `Imported ${ins} leads → assigned to ${assignedTo} (${dups} duplicates skipped)`
          : `Imported ${ins} leads (${dups} duplicates skipped)`
      );
      onImported(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl p-6 w-full max-w-xl border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Import leads</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="csv-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <label className="block">
              <span className="text-sm text-white/70 block mb-2">
                Auto-assign to agent <span className="text-white/40 text-xs font-normal">(optional)</span>
              </span>
              <select
                value={assignedAgentId}
                onChange={(e) => setAssignedAgentId(e.target.value)}
                data-testid="csv-agent-select"
                className="w-full bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400"
              >
                <option value="">— Leave unassigned —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.full_name || a.email}
                  </option>
                ))}
              </select>
              {assignedAgentId && (
                <p className="text-[11px] text-lime-300/80 mt-1.5">
                  ✓ Every imported lead will be pre-assigned to this agent — no manual bulk-assign needed.
                </p>
              )}
            </label>

            <label className="block">
              <span className="text-sm text-white/70 block mb-2">CSV or XLSX file</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm"
                onChange={onFile}
                data-testid="csv-file-input"
                className="block w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-lime-400 file:text-black file:font-semibold file:cursor-pointer"
              />
              {file && (
                <p className="text-xs text-white/50 mt-2 mono">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </label>

            <div className="p-3 rounded-lg bg-lime-400/5 border border-lime-400/20 space-y-2">
              <p className="text-xs text-white/70">
                <strong className="text-lime-300">Auto-mapped columns</strong> (case-insensitive):
              </p>
              <ul className="text-[11px] text-white/50 grid grid-cols-2 gap-x-3 gap-y-1 mono">
                <li>• Business Name / Company</li>
                <li>• Priority / Priority Score</li>
                <li>• Score (0-5)</li>
                <li>• Category</li>
                <li>• City / Area</li>
                <li>• Country</li>
                <li>• Phone</li>
                <li>• WhatsApp</li>
                <li>• Email</li>
                <li>• All Emails</li>
                <li>• LinkedIn</li>
                <li>• Website</li>
                <li>• Rating</li>
                <li>• Source</li>
                <li>• Tmpl + Subject + Analysis → notes</li>
              </ul>
              <a
                href="/gtx-leads-template.csv"
                download="gtx-leads-template.csv"
                data-testid="csv-template-download"
                className="inline-flex items-center gap-2 text-xs font-semibold text-lime-300 hover:text-lime-200 mt-1"
              >
                📥 Download CSV template
              </a>
            </div>

            <p className="text-xs text-white/40">
              Duplicates by exact <code>Business Name</code> are skipped automatically.
              Any unmapped columns are ignored.
            </p>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="border-white/10 text-white/80">Cancel</Button>
              <Button
                onClick={doImport}
                disabled={busy || !file}
                data-testid="csv-import-confirm"
                className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold"
              >
                {busy ? "Importing…" : `Import ${file?.name || ""}`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="import-result">
            <div className="grid grid-cols-3 gap-3">
              <ResultCard label="Imported" value={result.inserted || 0} color="text-emerald-300" />
              <ResultCard label="Duplicates" value={result.skipped_duplicates || 0} color="text-amber-300" />
              <ResultCard label="Total rows" value={result.total_rows || 0} color="text-white" />
            </div>
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <div className="text-xs text-red-300 max-h-40 overflow-y-auto p-2 rounded bg-red-500/10 border border-red-500/30">
                {result.errors.slice(0, 20).map((e, i) => <div key={`err-${i}`}>{e}</div>)}
                {result.errors.length > 20 && <div className="text-white/40">…+{result.errors.length - 20} more</div>}
              </div>
            )}
            <Button
              onClick={() => {
                setResult(null);
                setFile(null);
                onClose();
              }}
              className="w-full bg-lime-400 hover:bg-lime-300 text-black font-semibold"
              data-testid="import-result-close"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCard({ label, value, color }) {
  return (
    <div className="glass-strong rounded-lg p-3 border border-white/8 text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40 mono">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function FilterField({ label, value, onChange, options, type = "text", testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      {options ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
        >
          <option value="">All</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="bg-white/5 border-white/10 text-white mt-1"
        />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="bg-white/5 border-white/10 text-white mt-1"
      />
    </div>
  );
}


function BulkReassignDialog({ state, agents, loading, onForce, onSkip, onCancel, t }) {
  const { preview, target } = state;
  const targetName =
    agents.find((a) => a.id === target)?.full_name || t("leads.unassigned");
  const sample = preview.already_assigned_other_sample || [];
  // Map agent_id → name for the sample list
  const agentNameById = Object.fromEntries(agents.map((a) => [a.id, a.full_name]));
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="bulk-reassign-dialog"
    >
      <div className="glass-strong rounded-2xl p-6 w-full max-w-lg border border-amber-400/30 space-y-4">
        <div className="flex justify-between items-start gap-3">
          <div>
            <h2 className="text-lg font-bold text-amber-300">
              {t("assign.bulkConfirmTitle")}
            </h2>
            <p className="text-xs text-white/60 mt-1">
              {t("assign.bulkConfirmTo")}{" "}
              <span className="text-white font-semibold">{targetName}</span>
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-white/50 hover:text-white"
            data-testid="bulk-confirm-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3">
            <div className="text-2xl font-bold text-red-300 mono">
              {preview.already_assigned_other}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
              {t("assign.bulkAlreadyOther")}
            </div>
          </div>
          <div className="rounded-lg bg-slate-500/10 border border-slate-500/30 p-3">
            <div className="text-2xl font-bold text-slate-300 mono">
              {preview.already_assigned_target}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
              {t("assign.bulkAlreadyTarget")}
            </div>
          </div>
          <div className="rounded-lg bg-lime-500/10 border border-lime-500/30 p-3">
            <div className="text-2xl font-bold text-lime-300 mono">
              {preview.eligible}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
              {t("assign.bulkEligible")}
            </div>
          </div>
        </div>

        {sample.length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-lg border border-white/8 bg-white/[0.02] p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
              {t("assign.bulkSampleHeader")}
            </div>
            {sample.map((row) => (
              <div
                key={row.id}
                className="flex justify-between text-xs text-white/70"
              >
                <span className="truncate">{row.company_name}</span>
                <span className="text-white/40 ml-2 whitespace-nowrap">
                  → {agentNameById[row.current_agent_id] || "—"}
                </span>
              </div>
            ))}
            {preview.already_assigned_other > sample.length && (
              <div className="text-[10px] text-white/40 italic pt-1">
                + {preview.already_assigned_other - sample.length}{" "}
                {t("assign.bulkSampleMore")}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-white/20 text-white/80"
            disabled={loading}
            data-testid="bulk-confirm-cancel"
          >
            {t("assign.cancel")}
          </Button>
          <Button
            onClick={onSkip}
            variant="outline"
            disabled={loading || preview.eligible === 0}
            className="border-lime-400/40 text-lime-300 hover:bg-lime-400/10 disabled:opacity-50"
            data-testid="bulk-confirm-skip"
            title={t("assign.bulkOnlyFreeHint")}
          >
            {t("assign.bulkOnlyFree")} ({preview.eligible})
          </Button>
          <Button
            onClick={onForce}
            disabled={loading}
            className="bg-amber-400 hover:bg-amber-300 text-black font-semibold"
            data-testid="bulk-confirm-force"
          >
            {loading
              ? "…"
              : `${t("assign.bulkForce")} (${
                  preview.already_assigned_other + preview.eligible
                })`}
          </Button>
        </div>
      </div>
    </div>
  );
}
